import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ServiceGrid from '../components/services/ServiceGrid';
import ServiceModal from '../components/services/ServiceModal';
import { services } from '../data/services';
import { Service } from '../types/services';
import landingBackgroundImg from '../assets/images/Landing.webp';

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(${landingBackgroundImg}) no-repeat center center;
    background-size: cover;
    z-index: -2;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
`;

const FixedNavigation = styled.nav`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  gap: 2rem;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const PageHeading = styled(motion.h1)`
  text-align: center;
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 2rem;
  padding-top: 2rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageDescription = styled(motion.p)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  font-size: 1.2rem;
  color: #ffffff;
  line-height: 1.6;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ScrollToTopLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <NavLink to={to} onClick={handleClick}>
      {children}
    </NavLink>
  );
};

const ServicesPage: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
  };

  const handleCloseModal = () => {
    setSelectedService(null);
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <FixedNavigation>
          <ScrollToTopLink to="/">HOME</ScrollToTopLink>
          <ScrollToTopLink to="/about">ABOUT</ScrollToTopLink>
          <ScrollToTopLink to="/education">EDUCATION</ScrollToTopLink>
          <ScrollToTopLink to="/contact">CONTACT</ScrollToTopLink>
        </FixedNavigation>

        <PageHeading
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Our Services
        </PageHeading>
        
        <PageDescription
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Discover our comprehensive range of services designed to meet your construction and engineering needs. From comprehensive document management and retrieval to innovative solutions, we're here to help you build better.
        </PageDescription>

        <ServiceGrid services={services} onServiceSelect={handleServiceSelect} />
        
        <ServiceModal
          service={selectedService}
          onClose={handleCloseModal}
        />
      </ContentWrapper>
    </PageContainer>
  );
};

export default ServicesPage; 